// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Form } from "react-bootstrap";
import { NavigateFunction } from "react-router-dom";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { isSet } from "utils";
import { IAction, ActionType, raiseAction, IActionTypeChangeUser, IActionSortTable } from "actions";
import { UserRole } from "graphql/api";
import { UserAccessRights } from "dbtypes";
import { DataState, MainPanel } from "types";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";
import { Site, User } from "states/sitestates";
import { isStateDirty, makeSortHeader, toDropdownItems } from "guiutils";

import SaveFeedback from "view/comp/SaveFeedback";
import Dropdown from "view/comp/Dropdown";
import SearchFilter from "view/comp/SearchFilter";

// sort key of table
const TABLE_NAME = "t_user";

interface IEnlineUserAdminProps {
  loginRole: UserRole;
  loginCompany: string;
  user: User | undefined;
  isUserLoaded: boolean;
  allSites: Site[];
  userSites: number[];
  dataState: DataState;
  tableSort: ITableSort | undefined;
  saveError: string | undefined;
}

const mapStateToProps = (state: IState) => {
  // user to edit; if we can't find a user -> we will create a new one!
  const user = state.site.userList?.find((curUser) => state.router.user === curUser.id);

  // logged-in user
  const loginUser = state.site.userList?.find(
    (curUser) => curUser.secId === state.site.user?.secId,
  );

  const props: IEnlineUserAdminProps = {
    loginRole: loginUser?.role ?? UserRole.viewer,
    loginCompany: loginUser?.company ?? "embyt",
    user,
    isUserLoaded: isSet(state.scheduler.userListExt.lastData),
    allSites: state.site.siteList ?? [],
    userSites: user?.siteIds ?? [],
    dataState: state.scheduler.saveState,
    tableSort: state.gui.tableSort[TABLE_NAME],
    saveError: state.gui.login.userErrorText,
  };
  return props;
};

interface IEnlineUserAdminState {
  userId: number | undefined;
  wasUserLoaded: boolean;
  name: string | undefined;
  email: string | undefined;
  company: string;
  role: UserRole;
  userSites: number[];
  accessRights: UserAccessRights;
  filterText: string;
}

interface IEnlineUserAdminDispatch {
  onSort: (sort: IActionSortTable) => void;
  onCancelClick: (navigate: NavigateFunction) => void;
  onOkClick: (id: number | undefined, state: IEnlineUserAdminState) => void;
  onChanges: (dataState: DataState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnlineUserAdminDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onCancelClick: (navigate: NavigateFunction) => {
      // change location, which will also clear dirty flag and close the modal
      navigate(`../${MainPanel.USER}`, { state: { resetUnsavedChanges: true } });
    },
    onOkClick: (id: number | undefined, state: IEnlineUserAdminState) => {
      const payload: IActionTypeChangeUser = {
        id,
        email: state.email,
        name: state.name,
        company: state.company,
        role: state.role,
        userSites: state.userSites,
        accessRights: state.accessRights,
      };

      dispatch(raiseAction(ActionType.ACT_CHANGE_USER_REQ, payload));
    },
    onChanges: (dataState: DataState) => {
      if (!isStateDirty(dataState)) {
        dispatch(raiseAction(ActionType.ACT_SET_PAGE_DIRTY, true));
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectRole: {
    id: "Select role",
  },
  placeholderSearchFiled: {
    id: "Search for sites...",
  },
});

class UserAdminComponent extends React.Component<
  IEnlineUserAdminProps & IEnlineUserAdminDispatch & WrappedComponentProps & RoutedProps,
  IEnlineUserAdminState
> {
  state: IEnlineUserAdminState = {
    userId: this.props.user?.id,
    wasUserLoaded: this.props.isUserLoaded,
    email: this.props.user?.email,
    name: this.props.user?.name,
    company: this.props.user?.company ?? this.props.loginCompany,
    role: this.props.user?.role ?? this.state?.role ?? UserRole.viewer,
    accessRights: this.props.user?.accessRights ?? this.state?.accessRights ?? {},
    userSites: [...this.props.userSites],
    filterText: "",
  };

  public componentDidUpdate() {
    if (
      this.props.user?.id !== this.state.userId ||
      this.state.wasUserLoaded !== this.props.isUserLoaded
    ) {
      this.setState({
        userId: this.props.user?.id,
        wasUserLoaded: this.props.isUserLoaded,
        name: this.props.user?.name,
        role: this.props.user?.role ?? this.state?.role ?? UserRole.viewer,
        accessRights: this.props.user?.accessRights ?? this.state?.accessRights,
        userSites: [...this.props.userSites],
      });
    }
  }

  public render() {
    return (
      <div id="useradmin">
        <div className="savebox">
          {this.renderSaveChanges()}
          <SaveFeedback
            className="mb-2 mt-2 ms-2"
            dataState={
              isStateDirty(this.props.dataState) && !isSet(this.props.saveError)
                ? DataState.DIRTY
                : this.props.dataState
            }
            lastError={
              isSet(this.props.saveError)
                ? [this.props.intl.formatMessage({ id: this.props.saveError })]
                : undefined
            }
            hideDirty={!isSet(this.props.user)}
          />
        </div>
        {(isSet(this.props.user) ||
          [UserRole.admin, UserRole.root].includes(this.props.loginRole)) &&
          this.renderUserEdit()}
      </div>
    );
  }

  private renderUserEdit() {
    return (
      <>
        <h1>
          <FormattedMessage id="User Administration" />
        </h1>
        {this.renderUserTable()}
        <div className="tableSearchHeader" style={{ marginTop: 20 }}>
          <h1>
            <FormattedMessage id="Site List" />
          </h1>
          <SearchFilter
            className="tableSearchBox"
            filterText={this.state.filterText}
            placeholder={this.props.intl.formatMessage(messages.placeholderSearchFiled)}
            onChange={(filterText) => this.setState({ filterText })}
          />
        </div>
        {this.props.isUserLoaded ? this.renderSiteTable() : <FormattedMessage id="Loading..." />}
      </>
    );
  }

  private renderSaveChanges() {
    return (
      <div className="mb-2 mt-2">
        {isStateDirty(this.props.dataState) &&
          (isSet(this.props.user) || this.isValidEmail(this.state.email)) && (
            <Button
              variant="primary"
              id="saveUserAdmin"
              type="submit"
              className={`me-2 ${!isSet(this.props.user) ? "create_user" : ""}`}
              onClick={(_ev) => {
                this.props.onOkClick(this.props.user?.id, this.state);
              }}
            >
              {!isSet(this.props.user) ? (
                <FormattedMessage id="Create User" />
              ) : (
                <FormattedMessage id="Save Changes" />
              )}
            </Button>
          )}
        <Button
          variant="secondary"
          id="cancelUserAdmin"
          type="submit"
          onClick={(_ev) => {
            this.props.onCancelClick(this.props.navigate);
          }}
        >
          {isStateDirty(this.props.dataState) ? (
            <FormattedMessage id="Cancel and Back" />
          ) : (
            <FormattedMessage id="Back to User Overview" />
          )}
        </Button>
      </div>
    );
  }

  private renderUserTable() {
    const isEditMode = true;
    const roleItems = toDropdownItems(
      [UserRole.viewer, UserRole.admin],
      "value",
      (_name, value) => {
        this.setState({ role: value });
        this.props.onChanges(this.props.dataState);
      },
      (value) => this.props.intl.formatMessage({ id: `userrole_${value}` }),
    );

    return (
      <table className="fullwidthwhite">
        <tbody>
          {isEditMode && !isSet(this.props.user) && (
            <tr>
              <td>
                <FormattedMessage id="Email" />:
              </td>
              <td colSpan={2}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={this.state.email ?? ""}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(event) => {
                      this.setState({ email: event?.currentTarget.value });
                      this.props.onChanges(this.props.dataState);
                    }}
                  />
                ) : (
                  this.state.email
                )}
              </td>
            </tr>
          )}
          <tr>
            <td>
              <FormattedMessage id="User name" />:
            </td>
            <td colSpan={2}>
              {isEditMode ? (
                <input
                  type="text"
                  value={this.state.name ?? ""}
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                  onChange={(event) => {
                    this.setState({ name: event?.currentTarget.value });
                    this.props.onChanges(this.props.dataState);
                  }}
                />
              ) : (
                this.state.name
              )}
            </td>
          </tr>
          <tr>
            {isSet(this.props.user) && (
              <>
                <td>
                  <FormattedMessage id="Email Address" />:
                </td>
                <td colSpan={2}>{this.props.user?.email}</td>
              </>
            )}
          </tr>
          {this.props.loginRole === UserRole.root && (
            <tr>
              <td>
                <FormattedMessage id="Company" />:
              </td>
              {isSet(this.props.user) ? (
                <td colSpan={2}>{this.props.user?.company}</td>
              ) : (
                <td colSpan={2}>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={this.state.company ?? "embyt"}
                      disabled={this.props.dataState === DataState.SAVE_REQ}
                      onChange={(event) => {
                        this.setState({ company: event?.currentTarget.value });
                        this.props.onChanges(this.props.dataState);
                      }}
                    />
                  ) : (
                    this.state.company
                  )}
                </td>
              )}
            </tr>
          )}
          <tr>
            <td>
              <FormattedMessage id="Role" />:
            </td>
            <td colSpan={2}>
              {isEditMode && this.state.role !== UserRole.root ? (
                <Dropdown
                  className="btn-group-sm"
                  items={roleItems}
                  caption={this.props.intl.formatMessage(messages.selectRole)}
                  curItem={this.props.intl.formatMessage({ id: `userrole_${this.state.role}` })}
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                />
              ) : (
                <FormattedMessage id={`userrole_${this.state.role}`} />
              )}
            </td>
          </tr>
          <tr>
            <td rowSpan={13} style={{ verticalAlign: "top" }}>
              <FormattedMessage id="Special access rights" />:
            </td>
            <td>
              <Form.Label htmlFor="ackAlarms" className="link">
                <FormattedMessage id="Acknowledge Alarms" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("ackAlarms", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="modifyAlarms" className="link">
                <FormattedMessage id="Alarms" />: <FormattedMessage id="activate/edit alarms" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("modifyAlarms", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="importTrendData" className="link">
                <FormattedMessage id="Import Trend data" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("importTrendData", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="setVirtualInputs" className="link">
                <FormattedMessage id="Set Virtual Inputs" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("setVirtualInputs", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="switchOutputs" className="link">
                <FormattedMessage id="Control Outputs" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("switchOutputs", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="setParameter" className="link">
                <FormattedMessage id="Update Parameters" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("setParameter", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="ocppSettings" className="link">
                <FormattedMessage id="OCPP Change Settings" />:{" "}
                <FormattedMessage id="RFIDs, tariffs, chargers public/private" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("ocppSettings", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="ocppChargerAdmin" className="link">
                <FormattedMessage id="OCPP Administrate Chargers" />:{" "}
                <FormattedMessage id="delete charger, change connector names" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("ocppChargerAdmin", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="ocppChargerCtrl" className="link">
                <FormattedMessage id="OCPP Control Chargers" />:{" "}
                <FormattedMessage id="start/stop transactions, unlock connector, reboot charger" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("ocppChargerCtrl", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="showEnlineChargers" className="link">
                <FormattedMessage id="Energy Management Chargers" />:{" "}
                <FormattedMessage id="access energy management charger data" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("showEnlineChargers", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="controllerAdmin" className="link">
                <FormattedMessage id="Local Controller Admin" />:{" "}
                <FormattedMessage id="show extended controller data (modules, conditions) and allow reboot" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("controllerAdmin", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="createReports" className="link">
                <FormattedMessage id="Reports" />:{" "}
                <FormattedMessage id="create/edit/delete reports" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("createReports", isEditMode)}
          </tr>
          <tr>
            <td>
              <Form.Label htmlFor="crudTrendMonitors" className="link">
                <FormattedMessage id="Trend Monitors" />:{" "}
                <FormattedMessage id="create/edit/delete Trend Monitors" />
              </Form.Label>
            </td>
            {this.renderCheckBoxSettingsData("crudTrendMonitors", isEditMode)}
          </tr>
        </tbody>
      </table>
    );
  }

  private renderCheckBoxSettingsData(accessRight: keyof UserAccessRights, isEditMode: boolean) {
    return (
      <td>
        {isEditMode ? (
          <Form.Check
            name={accessRight}
            id={accessRight}
            checked={this.state.accessRights[accessRight] ? true : false}
            onChange={(event) => {
              const accessRights = { ...this.state.accessRights };
              accessRights[accessRight] = event.currentTarget.checked;
              this.setState({
                accessRights,
              });
              this.props.onChanges(this.props.dataState);
            }}
          />
        ) : this.props.user?.accessRights[accessRight] ? (
          <FormattedMessage id="yes" />
        ) : (
          <FormattedMessage id="no" />
        )}
      </td>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(column: keyof Site, caption: JSX.Element, classes?: string[]) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort,
      this.props.onSort,
      classes,
    );
  }

  private renderSiteTable() {
    const isEditMode = true;

    const sortCol = (this.props.tableSort?.column ?? "name") as keyof Site;
    const siteItems = [...this.props.allSites]
      .filter((site) =>
        site.name.toLocaleLowerCase().includes(this.state.filterText.toLocaleLowerCase()),
      )
      .sort((a, b) => {
        let keyA = sortCol === "cloudSettings" ? !this.state.userSites.includes(a.id) : a[sortCol];
        let keyB = sortCol === "cloudSettings" ? !this.state.userSites.includes(b.id) : b[sortCol];
        if (typeof keyA === "string" && typeof keyB === "string") {
          keyA = keyA.toLocaleLowerCase();
          keyB = keyB.toLocaleLowerCase();
        }
        const sort =
          isSet(keyA) && isSet(keyB)
            ? keyA === keyB
              ? a.name.localeCompare(b.name)
              : keyA > keyB
              ? 1
              : -1
            : isSet(keyB)
            ? 1
            : -1;
        return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
      })
      .map((curSite) => {
        return (
          <tr key={curSite.id}>
            <td>
              <Form.Label htmlFor={`siteEnabled_${curSite.id}`} className="link">
                {curSite.name}
              </Form.Label>
            </td>
            <td className="center checkbox">
              {isEditMode ? (
                <Form.Check
                  name="siteEnabled"
                  id={`siteEnabled_${curSite.id}`}
                  checked={
                    this.state.userSites.includes(curSite.id) ||
                    (this.state.role === UserRole.admin &&
                      this.props.user?.company === curSite.company)
                  }
                  disabled={
                    this.state.role === UserRole.root ||
                    (this.state.role === UserRole.admin &&
                      this.props.user?.company === curSite.company)
                  }
                  onChange={(event) => {
                    let userSites = this.state.userSites;
                    if (event.currentTarget.checked) {
                      userSites.push(curSite.id);
                    } else {
                      userSites = userSites.filter((site) => site !== curSite.id);
                    }
                    this.setState({ userSites });
                    this.props.onChanges(this.props.dataState);
                  }}
                />
              ) : this.state.userSites.includes(curSite.id) ? (
                <FormattedMessage id="yes" />
              ) : (
                <FormattedMessage id="no" />
              )}
            </td>
          </tr>
        );
      });

    return (
      <table className="datatable fullwidthwhite sitelist">
        <thead>
          <tr>
            {this.makeSortHeader("name", <FormattedMessage id="Site Name" />)}
            {this.makeSortHeader("cloudSettings", <FormattedMessage id="Access" />, [
              "center",
              "checkbox",
            ])}
          </tr>
        </thead>
        <tbody className="scrollbox">{siteItems}</tbody>
      </table>
    );
  }

  private isValidEmail(email: string | undefined): boolean {
    const isValid = isSet(email);
    return isValid;
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(UserAdminComponent)),
);
